import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { useRouter } from '@wbk/config';
import { Button, Input, Typography } from '@wbk/ui';
import { ChevronIcon } from '@wbk/svg';
import type { SelectOption } from './Select.types';

interface SingleSelectProps extends Omit<React.ComponentProps<typeof Input>, 'onChange' | 'value'> {
  options: SelectOption[];
  multiple?: false;
  onChange?: (value: SelectOption) => void;
  value?: SelectOption | string;
  autoSelect?: boolean;
  optionsClassName?: string;
  emptyText?: string;
  groupClassName?: string;
  renderLabel?: (option: SelectOption) => string;
  renderOption?: (option: SelectOption) => React.ReactNode;
  positionRef?: React.RefObject<HTMLDivElement | null>;
  portal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface MultiSelectProps extends Omit<React.ComponentProps<typeof Input>, 'onChange' | 'value'> {
  options: SelectOption[];
  multiple: true;
  onChange?: (value: SelectOption[]) => void;
  value?: SelectOption[] | string[];
  autoSelect?: boolean;
  optionsClassName?: string;
  emptyText?: string;
  groupClassName?: string;
  renderLabel?: (option: SelectOption) => string;
  renderOption?: (option: SelectOption) => React.ReactNode;
  positionRef?: React.RefObject<HTMLDivElement | null>;
  portal?: boolean;
  maxDisplayTags?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type Props = SingleSelectProps | MultiSelectProps;

const Select = ({
  label,
  labelVariant,
  onChange,
  error,
  options,
  className,
  optionsClassName,
  autoSelect,
  value,
  emptyText = 'No options found',
  groupClassName,
  renderOption,
  renderLabel,
  positionRef,
  portal = true,
  ref,
  ...props
}: Props) => {
  const MenuContainer = portal ? FloatingPortal : Fragment;
  const isMultiple = (props as MultiSelectProps).multiple;
  const maxDisplayTags = (props as MultiSelectProps).maxDisplayTags || 3;

  const selectedOptions = useMemo(() => {
    if (isMultiple) {
      const values = (value || []) as (SelectOption | string)[];
      return values
        .map((val) => {
          if ((val as SelectOption)?.value) {
            return val as SelectOption;
          }
          return options.find((o) => o.value === val);
        })
        .filter(Boolean) as SelectOption[];
    } else {
      const current = value as SelectOption | string;
      if ((current as SelectOption)?.value) {
        const index = options.findIndex((o) => o.value === (current as SelectOption).value);
        return {
          option: current as SelectOption,
          index,
        };
      }
      const index = options.findIndex((o) => o.value === current);
      return {
        option: options[index],
        index,
      };
    }
  }, [value, options, isMultiple]);

  const selectedLabel = useMemo(() => {
    if (isMultiple) {
      const selected = selectedOptions as SelectOption[];
      if (selected.length === 0) return '';
      return selected.map((opt) => renderLabel?.(opt) || opt.text || opt.value).join(', ');
    } else {
      const selected = selectedOptions as { option: SelectOption; index: number };
      return selected.option
        ? renderLabel?.(selected.option) || selected?.option?.text || selected?.option?.value
        : '';
    }
  }, [selectedOptions, isMultiple, renderLabel]);

  const [query, setQuery] = useState('');
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = props.open ?? internalIsOpen;
  const setIsOpen = (open: boolean) => {
    setInternalIsOpen(open);
    props.onOpenChange?.(open);
  };

  const [activeIndex, setActiveIndex] = useState<number | null>(
    isMultiple ? 0 : (selectedOptions as { option: SelectOption; index: number }).index
  );
  const { locale } = useRouter();
  const { t } = useTranslation('common');

  const filteredOptions = useMemo(() => {
    if (!query) return options;

    return options.filter((o) => {
      const text = (o?.text || '').toLowerCase();
      const value = o.value.toLowerCase();
      const q = query.toLowerCase();

      return (
        text.includes(q) ||
        value.includes(q) ||
        [text, value].some((str) => {
          for (let i = 0; i <= str.length - q.length; i++) {
            const slice = str.substring(i, i + q.length);
            if (slice.localeCompare(q, locale, { sensitivity: 'base' }) === 0) {
              return true;
            }
          }
          return false;
        })
      );
    });
  }, [query, options, locale]);

  const { refs, floatingStyles, context } = useFloating<HTMLDivElement>({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxWidth: `${rects.reference.width}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const handleSelect = (option: SelectOption) => {
    if (isMultiple) {
      const selected = selectedOptions as SelectOption[];
      const isSelected = selected.some((opt) => opt.value === option.value);
      const newValues = isSelected
        ? selected.filter((opt) => opt.value !== option.value)
        : [...selected, option];
      (onChange as (value: SelectOption[]) => void)?.(newValues);
    } else {
      (onChange as (value: SelectOption) => void)?.(option);
      setIsOpen(false);
    }
  };

  const handleRemoveTag = (optionToRemove: SelectOption, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMultiple) {
      const selected = selectedOptions as SelectOption[];
      const newValues = selected.filter((opt) => opt.value !== optionToRemove.value);
      (onChange as (value: SelectOption[]) => void)?.(newValues);
    }
  };

  const handleSearchChange = (value: string) => {
    setQuery(value);
    setActiveIndex(0);
  };

  const isOptionSelected = (option: SelectOption) => {
    if (isMultiple) {
      const selected = selectedOptions as SelectOption[];
      return selected.some((opt) => opt.value === option.value);
    } else {
      const selected = selectedOptions as { option: SelectOption; index: number };
      return selected?.option?.value === option.value;
    }
  };

  useImperativeHandle(ref, () => refs.domReference.current as unknown as HTMLInputElement);

  useEffect(() => {
    if (positionRef?.current) {
      refs.setPositionReference(positionRef.current);
    }
  }, [positionRef, refs]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  const listRef = useRef<Array<HTMLElement | null>>([]);

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: isMultiple
      ? -1
      : options.findIndex(
          (o) =>
            o.value === (selectedOptions as { option: SelectOption; index: number })?.option?.value
        ),
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    dismiss,
    role,
    listNav,
  ]);

  const displayedTags = useMemo(() => {
    if (!isMultiple) return [];
    const selected = selectedOptions as SelectOption[];
    return selected.slice(0, maxDisplayTags);
  }, [selectedOptions, isMultiple, maxDisplayTags]);

  const remainingTagsCount = useMemo(() => {
    if (!isMultiple) return 0;
    const selected = selectedOptions as SelectOption[];
    return Math.max(0, selected.length - maxDisplayTags);
  }, [selectedOptions, isMultiple, maxDisplayTags]);

  const hasSelectedIcon = useMemo(() => {
    if (isMultiple) return false;
    const selected = selectedOptions as { option: SelectOption; index: number };
    return !!selected?.option?.icon;
  }, [selectedOptions, isMultiple]);

  return (
    <div className={groupClassName}>
      {label && labelVariant !== 'floating' && (
        <label
          className={`block pb-1 font-semibold ${props.disabled ? 'text-text-secondary' : ''}`}
          htmlFor={props.id}
          role='presentation'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {label}
        </label>
      )}

      <div className='relative' ref={refs.setReference}>
        {isMultiple && displayedTags.length > 0 && (
          <div
            role='button'
            tabIndex={0}
            className='bg-input border-input-border text-text rounded-S -mb-4 flex cursor-pointer flex-wrap items-center gap-1.5 border p-2'
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(!isOpen);
              }
            }}
          >
            {displayedTags.map((tag) => (
              <span
                key={tag.value}
                className='inline-flex items-center gap-1 rounded bg-white/10 px-2 py-1'
              >
                {tag.icon && <span className='flex-shrink-0'>{tag.icon}</span>}
                <Typography className='max-w-[100px] truncate sm:max-w-[150px]' variant='body-S'>
                  {tag.text || tag.value}
                </Typography>
                <button
                  type='button'
                  onClick={(e) => handleRemoveTag(tag, e)}
                  className='flex-shrink-0 rounded-full p-0.5 transition-colors hover:bg-white/20'
                  aria-label={`Remove ${tag.text || tag.value}`}
                >
                  <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </span>
            ))}
            {remainingTagsCount > 0 && (
              <span className='inline-flex items-center rounded bg-white/10 px-2 py-1'>
                <Typography variant='body-S'>
                  {t('validation.more_selected', { count: remainingTagsCount })}
                </Typography>
              </span>
            )}
          </div>
        )}

        <Input
          aria-labelledby='select-label'
          aria-autocomplete='none'
          className={twMerge(
            hasSelectedIcon && 'ltr:pl-8 rtl:pr-8',
            'text-ellipsis ltr:pr-[10%] ltr:md:pr-8 rtl:pl-[18%] rtl:md:pl-8',
            isMultiple && displayedTags.length > 0 && 'sr-only',
            className
          )}
          value={isOpen ? '' : selectedLabel}
          readOnly
          error={error}
          label={labelVariant === 'floating' ? label : undefined}
          labelVariant={labelVariant}
          {...props}
          {...getReferenceProps({
            onKeyDown: (event) => {
              if (event.key === 'Enter' && !isOpen) return setIsOpen(true);
              const selected = typeof activeIndex === 'number' && filteredOptions[activeIndex];
              if (event.key === 'Enter' && selected) {
                event.preventDefault();
                handleSelect(selected);
              }
            },
            onClick() {
              setIsOpen(!isOpen);
            },
            onFocus(e: React.FocusEvent<HTMLInputElement>) {
              if (autoSelect) {
                e.target.select();
              }
            },
          })}
        />
        {!isMultiple && hasSelectedIcon && (
          <span className='absolute top-0 mt-[calc(var(--spacing-XS)*1.5)] flex items-center px-2 disabled:cursor-not-allowed ltr:left-0 rtl:right-0'>
            {(selectedOptions as { option: SelectOption; index: number })?.option.icon}
          </span>
        )}
        <Button
          theme='transparent'
          shape='text'
          className='mt-XS data-[error=true]:ltr:right-M data-[error=true]:rtl:left-M absolute top-0 !bg-transparent p-2 data-[floating=true]:mt-[calc(var(--spacing-XS)*1.2)] ltr:right-0 rtl:left-0'
          aria-label={`Open ${props.name}`}
          data-error={!!error}
          data-floating={label && labelVariant === 'floating'}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          tabIndex={-1}
          disabled={props.disabled}
          type='button'
        >
          <ChevronIcon className='h-3 w-3 rotate-90' />
        </Button>
      </div>

      <MenuContainer>
        {isOpen && (
          <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
            <div
              ref={refs.setFloating}
              className={twMerge(
                'mini-scrollbar bg-input-popover border-input-border text-text rounded-S isolation-auto z-[99] max-h-72 overflow-y-auto border pb-1 pt-0 shadow-lg focus:outline-none sm:text-sm md:max-h-60',
                optionsClassName
              )}
              style={{
                ...floatingStyles,
                position: portal ? floatingStyles.position : 'fixed',
              }}
              {...getFloatingProps()}
            >
              <div className='border-input-border sticky top-0 z-10 border-b bg-inherit p-2'>
                <Input
                  placeholder='Search...'
                  value={query}
                  onChange={handleSearchChange}
                  className='!bg-input-popover'
                />
              </div>
              {filteredOptions.length === 0 && (
                <p className='text-text-secondary p-4 text-center'>{emptyText}</p>
              )}
              {filteredOptions.map((option, i) => {
                const selected = isOptionSelected(option);
                return (
                  <div
                    key={option.value}
                    ref={(node) => {
                      listRef.current[i] = node;
                    }}
                    role='option'
                    tabIndex={i === activeIndex ? 0 : -1}
                    aria-selected={i === activeIndex || selected}
                    className={twMerge(
                      'flex cursor-pointer select-none items-center justify-between gap-2 px-4 py-2 outline-none',
                      i === activeIndex && 'bg-primary-400 text-primary-contrast',
                      !isMultiple && 'space-x-2 rtl:space-x-reverse'
                    )}
                    {...getItemProps({
                      onClick() {
                        handleSelect(option);
                        if (!isMultiple) {
                          refs.domReference.current?.focus();
                        }
                      },
                    })}
                  >
                    <div className='flex min-w-0 flex-1 items-center gap-2'>
                      {renderOption ? (
                        renderOption(option)
                      ) : (
                        <>
                          {option.icon && <span className='flex-shrink-0'>{option.icon}</span>}
                          <span
                            className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                          >
                            {option.text || option.value}
                          </span>
                        </>
                      )}
                    </div>
                    {isMultiple && selected && (
                      <svg
                        className='h-4 w-4 flex-shrink-0'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M5 13l4 4L19 7'
                        />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          </FloatingFocusManager>
        )}
      </MenuContainer>
    </div>
  );
};

export default Select;
